import { OCCASIONS } from '@ly360/core';
import { zonedWallTimeToIso } from '@ly360/i18n';
import { ChannelIcon, DateTimeField, Dialog, ErrorState, Select, useFormat } from '@ly360/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Megaphone, MessageSquare, Newspaper } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Field } from '../crm/forms';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import {
  draftIssues,
  draftPayload,
  emptyDraft,
  PostComposer,
  useDestinations,
  type PostDraft,
} from '../publishing/Composer';
import { RETAIL_KEYS, useSegments, type Reach, type Rule } from './data';
import { DialogButtons } from './ui';

/** Pre-filled campaign (from a sales opportunity or an occasion): the audience can be a new segment's rules. */
export interface CampaignPreset {
  name: string;
  kind?: 'broadcast' | 'paid';
  segmentId?: string;
  /** Creates this segment on save (when no existing segment is chosen). */
  segmentName?: string;
  rules?: Rule[];
  occasion?: string;
  templateName?: string;
  /** Wall time in the organisation's zone, "YYYY-MM-DDTHH:mm". */
  startsAt?: string;
}

interface Options {
  channels: { channel: 'whatsapp' | 'email' | 'sms'; connected: boolean; available: boolean }[];
  templates: { name: string; category: string; languages: Record<string, string> }[];
  platforms: string[];
}

type Part = 'post' | 'messages' | 'ads';
const PART_ICON = { post: Newspaper, messages: MessageSquare, ads: Megaphone };

/**
 * One campaign, everywhere at once: tick where it goes (social posts on every account, messages on several
 * channels, the paid ads it runs), with one name, audience, offer code and results page.
 */
export function NewCampaignDialog({
  open,
  onClose,
  preset,
}: {
  open: boolean;
  onClose: () => void;
  preset?: CampaignPreset;
}) {
  const { t, i18n } = useTranslation();
  const fmt = useFormat();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { can } = useAuth();
  const segments = useSegments();
  const destinations = useDestinations();
  const options = useQuery({
    queryKey: ['campaigns', 'options'],
    queryFn: () => api<Options>('/v1/campaigns/options'),
  });
  const usePresetAudience = !!preset?.rules && !preset.segmentId;
  const [parts, setParts] = useState<Set<Part>>(
    new Set(preset?.kind === 'paid' ? ['ads'] : preset ? ['messages'] : ['post', 'messages']),
  );
  const [f, setF] = useState({
    name: preset?.name ?? '',
    objective: 'sales',
    segmentId: preset?.segmentId ?? (usePresetAudience ? 'preset' : ''),
    offerCode: '',
    occasion: preset?.occasion ?? '',
    startsAt: preset?.startsAt ?? '',
    endsAt: '',
    channels: ['whatsapp'] as ('whatsapp' | 'email' | 'sms')[],
    templateName: preset?.templateName ?? '',
    messageEn: '',
    messageAr: '',
  });
  const [ads, setAds] = useState<Record<string, { adIds: string; budget: string }>>({});
  const [draft, setDraft] = useState<PostDraft>(() => ({ ...emptyDraft(), when: 'draft' }));
  const set = (p: Partial<typeof f>) => setF((x) => ({ ...x, ...p }));
  const toggle = (p: Part) =>
    setParts((s) => (s.has(p) ? new Set([...s].filter((x) => x !== p)) : new Set([...s, p])));
  const preview = useQuery({
    queryKey: ['segments', 'preview', preset?.rules],
    enabled: usePresetAudience,
    queryFn: () =>
      api<{ reach: Reach }>('/v1/segments/preview', {
        method: 'POST',
        body: JSON.stringify({ rules: { all: preset!.rules } }),
      }),
  });
  const reach: Reach | undefined =
    f.segmentId === 'preset'
      ? preview.data?.reach
      : segments.data?.items.find((s) => s.id === f.segmentId)?.reach;
  const templates = options.data?.templates ?? [];
  const template = templates.find((x) => x.name === f.templateName);
  const destItems = destinations.data?.items ?? [];
  const postBlocked =
    parts.has('post') &&
    (!draft.targets.length || draftIssues(draft, destItems).some((i) => i.errors.length));
  const iso = (v: string) => (v ? zonedWallTimeToIso(v, fmt.timezone) : null);

  const save = useMutation({
    mutationFn: async () => {
      let segmentId: string | null = f.segmentId && f.segmentId !== 'preset' ? f.segmentId : null;
      if (f.segmentId === 'preset' && preset?.rules)
        segmentId = (
          await api<{ id: string }>('/v1/segments', {
            method: 'POST',
            body: JSON.stringify({ name: preset.segmentName ?? f.name, rules: { all: preset.rules } }),
          })
        ).id;
      return api<{ id: string }>('/v1/campaigns', {
        method: 'POST',
        body: JSON.stringify({
          name: f.name,
          objective: f.objective,
          segmentId,
          offerCode: f.offerCode.trim() || null,
          occasion: f.occasion || null,
          startsAt: iso(f.startsAt),
          endsAt: iso(f.endsAt),
          messages: parts.has('messages')
            ? {
                channels: f.channels,
                templateName: f.channels.includes('whatsapp') ? f.templateName || null : null,
                messageEn: f.messageEn || null,
                messageAr: f.messageAr || null,
              }
            : null,
          ads: parts.has('ads')
            ? Object.entries(ads).map(([platform, a]) => ({
                platform,
                adIds: a.adIds
                  .split(/[\s,]+/)
                  .map((x) => x.trim())
                  .filter(Boolean),
                budget: a.budget ? Number(a.budget) : null,
              }))
            : [],
          post: parts.has('post') ? draftPayload(draft, iso) : null,
        }),
      });
    },
    onSuccess: (r) => {
      for (const k of [...RETAIL_KEYS, 'posts']) void qc.invalidateQueries({ queryKey: [k] });
      onClose();
      navigate(`/campaigns/${r.id}`);
    },
  });

  const ar = i18n.language === 'ar';
  const channelOptions = options.data?.channels ?? [];
  return (
    <Dialog
      open={open}
      wide
      title={t('retail.campaign.new')}
      onClose={onClose}
      onSubmit={() => save.mutate()}
      testId="new-campaign"
      footer={
        <DialogButtons
          onClose={onClose}
          busy={save.isPending}
          disabled={!parts.size || postBlocked || (parts.has('ads') && !Object.keys(ads).length)}
          label={t('retail.campaign.create')}
        />
      }
    >
      <div className="form-grid">
        <Field id="cf-name" label={t('retail.campaign.name')}>
          <input
            id="cf-name"
            className="input"
            required
            value={f.name}
            onChange={(e) => set({ name: e.target.value })}
          />
        </Field>
        <Field id="cf-segment" label={t('retail.campaign.audience')} hint={t('retail.campaign.audienceHint')}>
          <Select
            id="cf-segment"
            value={f.segmentId}
            onChange={(v) => set({ segmentId: v })}
            placeholder={t('retail.campaign.chooseAudience')}
            options={[
              ...(usePresetAudience
                ? [
                    {
                      value: 'preset',
                      label: preset!.segmentName ?? preset!.name,
                      hint: t('retail.campaign.newAudience'),
                    },
                  ]
                : []),
              { value: '', label: t('retail.campaign.noAudience') },
              ...(segments.data?.items ?? []).map((s) => ({
                value: s.id,
                label: (ar && s.nameAr) || s.name,
                hint: t('retail.segment.people', { count: s.reach.total }),
              })),
            ]}
          />
        </Field>
        <Field id="cf-code" label={t('retail.campaign.offerCode')} hint={t('retail.campaign.offerCodeHint')}>
          <input
            id="cf-code"
            className="input mono"
            dir="ltr"
            pattern="[A-Za-z0-9_\-]{2,40}"
            value={f.offerCode}
            onChange={(e) => set({ offerCode: e.target.value.toUpperCase() })}
          />
        </Field>
        <Field id="cf-occasion" label={t('retail.campaign.occasion')}>
          <Select
            id="cf-occasion"
            value={f.occasion}
            onChange={(v) => set({ occasion: v })}
            options={[
              { value: '', label: t('common.none') },
              ...OCCASIONS.map((o) => ({ value: o, label: t(`retail.occasion.${o}`) })),
            ]}
          />
        </Field>
        <Field id="cf-objective" label={t('retail.campaign.objective')}>
          <Select
            id="cf-objective"
            value={f.objective}
            onChange={(v) => set({ objective: v })}
            options={(['sales', 'messages', 'leads', 'retention', 'traffic', 'awareness'] as const).map(
              (o) => ({ value: o, label: t(`retail.objective.${o}`) }),
            )}
          />
        </Field>
        <Field id="cf-start" label={t('retail.campaign.startsAt')} hint={fmt.zoneCaption}>
          <DateTimeField id="cf-start" value={f.startsAt} onChange={(v) => set({ startsAt: v })} />
        </Field>
      </div>

      <h3 className="section-title" style={{ marginTop: '1rem' }}>
        {t('retail.campaign.whereItGoes')}
      </h3>
      <div className="part-picker" role="group" aria-label={t('retail.campaign.whereItGoes')}>
        {(['post', 'messages', 'ads'] as const).map((p) => {
          const Icon = PART_ICON[p];
          const disabled = p === 'post' && !can('posts:write');
          return (
            <label key={p} className={`part-tile${parts.has(p) ? ' is-on' : ''}`} data-testid={`part-${p}`}>
              <input type="checkbox" checked={parts.has(p)} disabled={disabled} onChange={() => toggle(p)} />
              <Icon size={18} aria-hidden="true" />
              <span>
                <strong>{t(`retail.campaign.parts.${p}`)}</strong>
                <span className="faint">{t(`retail.campaign.partsHint.${p}`)}</span>
              </span>
            </label>
          );
        })}
      </div>

      {parts.has('post') && (
        <section className="part-section" data-testid="section-post">
          <h4>{t('retail.campaign.parts.post')}</h4>
          <PostComposer draft={draft} setDraft={setDraft} destinations={destItems} />
        </section>
      )}

      {parts.has('messages') && (
        <section className="part-section" data-testid="section-messages">
          <h4>{t('retail.campaign.parts.messages')}</h4>
          <p className="faint" style={{ marginTop: 0 }}>
            {t('retail.campaign.channelsHint')}
          </p>
          <div className="channel-order" role="group" aria-label={t('retail.campaign.channel')}>
            {channelOptions.map((c) => {
              const at = f.channels.indexOf(c.channel);
              return (
                <label
                  key={c.channel}
                  className={`chip chip-toggle${!c.available ? ' is-disabled' : ''}`}
                  title={
                    !c.available
                      ? t('retail.campaign.soon')
                      : !c.connected
                        ? t('retail.campaign.notConnected')
                        : undefined
                  }
                >
                  <input
                    type="checkbox"
                    disabled={!c.available}
                    checked={at >= 0}
                    onChange={(e) =>
                      set({
                        channels: e.target.checked
                          ? [...f.channels, c.channel]
                          : f.channels.filter((x) => x !== c.channel),
                      })
                    }
                    data-testid={`msg-${c.channel}`}
                  />
                  {at >= 0 && <span className="order-num">{at + 1}</span>}
                  <ChannelIcon channel={c.channel} size={11} />
                  {t(`channels.${c.channel}`)}
                </label>
              );
            })}
          </div>
          {reach && (
            <div
              className="notice"
              role="status"
              data-testid="campaign-reach"
              style={{ marginTop: '0.75rem' }}
            >
              {t('retail.campaign.reachMulti', {
                total: fmt.number(reach.total),
                parts: f.channels
                  .map((c) => `${t(`channels.${c}`)} ${fmt.number(reach[c] ?? 0)}`)
                  .join(' · '),
              })}
            </div>
          )}
          {f.channels.includes('whatsapp') && (
            <Field
              id="cf-template"
              label={t('retail.campaign.template')}
              hint={t('retail.campaign.templateHint')}
            >
              <Select
                id="cf-template"
                value={f.templateName}
                onChange={(v) => set({ templateName: v })}
                placeholder={t('retail.campaign.chooseTemplate')}
                options={templates.map((x) => ({
                  value: x.name,
                  label: x.name,
                  hint: `${t(`retail.templateCategory.${x.category}`)} · ${Object.keys(x.languages).join(', ').toUpperCase()}`,
                }))}
              />
            </Field>
          )}
          {template && (
            <div className="template-preview" data-testid="template-preview">
              {Object.entries(template.languages).map(([lang, body]) => (
                <div
                  key={lang}
                  className="bubble out"
                  dir={lang.startsWith('ar') ? 'rtl' : 'ltr'}
                  lang={lang}
                >
                  {body}
                </div>
              ))}
              <p className="faint" style={{ fontSize: 'var(--t-xs)', margin: 0 }}>
                {t('retail.campaign.templateVars')}
              </p>
            </div>
          )}
          {f.channels.some((c) => c !== 'whatsapp') && (
            <div className="form-grid">
              <Field
                id="cf-msg-en"
                label={t('retail.campaign.messageEn')}
                hint={t('retail.campaign.messageVars', { nameVar: '{{name}}', codeVar: '{{code}}' })}
              >
                <textarea
                  id="cf-msg-en"
                  className="textarea"
                  rows={4}
                  value={f.messageEn}
                  onChange={(e) => set({ messageEn: e.target.value })}
                />
              </Field>
              <Field id="cf-msg-ar" label={t('retail.campaign.messageAr')}>
                <textarea
                  id="cf-msg-ar"
                  className="textarea"
                  rows={4}
                  dir="rtl"
                  lang="ar"
                  value={f.messageAr}
                  onChange={(e) => set({ messageAr: e.target.value })}
                />
              </Field>
            </div>
          )}
        </section>
      )}

      {parts.has('ads') && (
        <section className="part-section" data-testid="section-ads">
          <h4>{t('retail.campaign.parts.ads')}</h4>
          <p className="faint" style={{ marginTop: 0 }}>
            {t('retail.campaign.adsHint')}
          </p>
          <div className="dest-grid">
            {(options.data?.platforms ?? []).map((p) => {
              const on = !!ads[p];
              return (
                <div key={p} className={`dest-tile ad-tile${on ? ' is-on' : ''}`}>
                  <label className="ad-tile-head" data-testid={`ad-${p}`}>
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={(e) =>
                        setAds((x) => {
                          const n = { ...x };
                          if (e.target.checked) n[p] = { adIds: '', budget: '' };
                          else delete n[p];
                          return n;
                        })
                      }
                    />
                    <ChannelIcon channel={p} size={16} />
                    <strong>{t(`retail.platformShort.${p}`)}</strong>
                  </label>
                  {on && (
                    <div className="ad-fields">
                      <input
                        className="input mono"
                        dir="ltr"
                        placeholder={t('retail.campaign.adIds')}
                        aria-label={`${t('retail.campaign.adIds')} (${t(`retail.platformShort.${p}`)})`}
                        value={ads[p]!.adIds}
                        onChange={(e) => setAds((x) => ({ ...x, [p]: { ...x[p]!, adIds: e.target.value } }))}
                      />
                      <input
                        className="input"
                        type="number"
                        min={0}
                        inputMode="decimal"
                        placeholder={t('retail.campaign.budget')}
                        aria-label={`${t('retail.campaign.budget')} (${t(`retail.platformShort.${p}`)})`}
                        value={ads[p]!.budget}
                        onChange={(e) => setAds((x) => ({ ...x, [p]: { ...x[p]!, budget: e.target.value } }))}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}
      {save.isError && <ErrorState error={save.error} />}
    </Dialog>
  );
}
